/**
 * AssignBenevolesModal — assigne des bénévoles à un événement (culte).
 * Un Select par équipe + recherche fuzzy de membres + ajout en pastilles.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import Fuse from "fuse.js";
import Modal from "./Modal";
import Button from "./Button";
import Select from "./Select";
import Avatar from "./Avatar";
import { colors, fonts } from "../styles/tokens";
import { benevolesApi } from "../services/api";
import useMembers from "../hooks/useMembers";
import useToast from "../hooks/useToast";

export default function AssignBenevolesModal({ open, onClose, evenement, onChange }) {
  const { members } = useMembers();
  const toast = useToast();

  const [equipes, setEquipes] = useState([]);
  const [equipeId, setEquipeId] = useState("");
  const [role, setRole] = useState("");
  const [search, setSearch] = useState("");
  const [picks, setPicks] = useState([]);   // [{ membre_id, nom, equipe_id, role }]
  const [existing, setExisting] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    if (!evenement) return;
    try {
      const [eqRes, plRes] = await Promise.all([
        benevolesApi.listEquipes({ eglise_id: evenement.eglise_id }),
        benevolesApi.planningEvenement(evenement.id),
      ]);
      setEquipes(eqRes.data || []);
      setExisting(plRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    }
  }, [evenement, toast]);

  useEffect(() => {
    if (open) {
      reload();
      setPicks([]);
      setSearch("");
      setEquipeId("");
      setRole("");
    }
  }, [open, reload]);

  // Membres éligibles : même église
  const eligibleMembers = useMemo(() => {
    if (!evenement) return [];
    return members.filter((m) => m.eglise_id === evenement.eglise_id);
  }, [members, evenement]);

  const fuse = useMemo(
    () => new Fuse(eligibleMembers, {
      keys: ["prenom", "nom", "full_name", "telephone"],
      threshold: 0.35,
      minMatchCharLength: 2,
    }),
    [eligibleMembers]
  );

  const searchResults = useMemo(() => {
    if (search.trim().length < 2) return [];
    return fuse.search(search.trim()).map((r) => r.item).slice(0, 6);
  }, [search, fuse]);

  const addPick = (member) => {
    if (!equipeId) {
      toast.info("Sélectionnez d'abord une équipe");
      return;
    }
    if (picks.some((p) => p.membre_id === member.id && p.equipe_id === Number(equipeId))) {
      toast.info(`${member.prenom} déjà sélectionné·e pour cette équipe`);
      return;
    }
    const eq = equipes.find((e) => String(e.id) === equipeId);
    setPicks((prev) => [...prev, {
      membre_id: member.id,
      nom: `${member.prenom} ${member.nom}`,
      equipe_id: Number(equipeId),
      equipe_nom: eq?.nom || "",
      equipe_couleur: eq?.couleur || colors.gold,
      role: role.trim() || null,
    }]);
    setSearch("");
  };

  const removePick = (idx) => {
    setPicks((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!picks.length) {
      toast.info("Aucune assignation à enregistrer");
      return;
    }
    setSubmitting(true);
    try {
      await benevolesApi.createPlanningBatch({
        assignations: picks.map((p) => ({
          equipe_id: p.equipe_id,
          membre_id: p.membre_id,
          role: p.role,
          date_service: (evenement.date_debut || "").slice(0, 10),
          evenement_id: evenement.id,
        })),
      });
      toast.success(`${picks.length} assignation${picks.length > 1 ? "s" : ""} enregistrée${picks.length > 1 ? "s" : ""}`);
      onChange?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveExisting = async (planning) => {
    if (!window.confirm(`Retirer ${planning.membre_nom} de ${planning.equipe_nom} ?`)) return;
    try {
      await benevolesApi.removePlanning(planning.id);
      toast.success("Assignation supprimée");
      reload();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Existing groupés par équipe
  const existingByEquipe = useMemo(() => {
    const m = {};
    existing.forEach((p) => {
      if (!m[p.equipe_id]) m[p.equipe_id] = [];
      m[p.equipe_id].push(p);
    });
    return m;
  }, [existing]);

  if (!evenement) return null;

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title="Assigner des bénévoles"
      subtitle={`${evenement.titre} — ${new Date(evenement.date_debut).toLocaleDateString("fr-FR")}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Fermer</Button>
          {picks.length > 0 && (
            <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
              Enregistrer {picks.length} assignation{picks.length > 1 ? "s" : ""}
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* Assignations existantes */}
        {existing.length > 0 && (
          <div>
            <div style={sectionTitle}>Assignations existantes ({existing.length})</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {Object.entries(existingByEquipe).map(([eqId, items]) => {
                const first = items[0];
                return (
                  <div key={eqId} style={{
                    padding: "10px 12px",
                    background: colors.bg,
                    borderRadius: "10px",
                    borderLeft: `3px solid ${first.equipe_couleur}`,
                  }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "6px", color: first.equipe_couleur }}>
                      {first.equipe_nom}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {items.map((p) => (
                        <div key={p.id} style={{
                          display: "inline-flex", alignItems: "center", gap: "8px",
                          padding: "6px 10px",
                          background: colors.surface,
                          border: `1px solid ${colors.border}`,
                          borderRadius: "999px",
                          fontSize: "12px",
                        }}>
                          <StatutDot statut={p.statut} />
                          <span>{p.membre_nom}</span>
                          {p.role && <span style={{ color: colors.textMuted }}>· {p.role}</span>}
                          <button
                            onClick={() => handleRemoveExisting(p)}
                            style={{ color: colors.textMuted, fontSize: "14px", padding: "0 4px", lineHeight: 1 }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Nouvelle assignation */}
        <div>
          <div style={sectionTitle}>Ajouter des bénévoles</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
            <Select
              label="Équipe"
              value={equipeId}
              onChange={setEquipeId}
              options={equipes.map((e) => ({ value: String(e.id), label: e.nom }))}
              placeholder="Sélectionnez une équipe..."
              required
            />
            <input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Rôle (ex : Lead vocal)"
              style={{
                background: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: "10px",
                padding: "12px 14px",
                fontSize: "14px",
                outline: "none",
                fontFamily: fonts.body,
                alignSelf: "end",
              }}
              onFocus={(e) => { e.target.style.borderColor = colors.gold; }}
              onBlur={(e)  => { e.target.style.borderColor = colors.border; }}
            />
          </div>

          <div style={{ position: "relative" }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={equipeId ? "Rechercher un membre..." : "Choisissez d'abord une équipe"}
              disabled={!equipeId}
              style={{
                background: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: "10px",
                padding: "12px 14px",
                fontSize: "14px",
                outline: "none",
                fontFamily: fonts.body,
                width: "100%",
                opacity: equipeId ? 1 : 0.5,
              }}
            />
            {searchResults.length > 0 && (
              <div style={{
                marginTop: "6px",
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: "10px",
                overflow: "hidden",
              }}>
                {searchResults.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => addPick(m)}
                    style={{
                      display: "flex", alignItems: "center", gap: "10px",
                      width: "100%", padding: "8px 12px",
                      background: "transparent", textAlign: "left",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.goldDim; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <Avatar name={`${m.prenom} ${m.nom}`} size="xs" />
                    <span style={{ fontSize: "14px" }}>{m.prenom} {m.nom}</span>
                    <span style={{ marginLeft: "auto", color: colors.gold, fontSize: "13px" }}>+ Ajouter</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Picks en attente d'envoi */}
          {picks.length > 0 && (
            <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {picks.map((p, idx) => (
                <div key={idx} style={{
                  display: "inline-flex", alignItems: "center", gap: "8px",
                  padding: "6px 10px",
                  background: colors.bg,
                  border: `1px solid ${p.equipe_couleur}`,
                  borderRadius: "999px",
                  fontSize: "13px",
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: p.equipe_couleur,
                  }} />
                  <span style={{ fontWeight: 500 }}>{p.nom}</span>
                  <span style={{ color: colors.textMuted }}>· {p.equipe_nom}</span>
                  {p.role && <span style={{ color: colors.textMuted }}>· {p.role}</span>}
                  <button
                    onClick={() => removePick(idx)}
                    style={{ color: colors.textMuted, fontSize: "14px", padding: "0 4px", lineHeight: 1 }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

const sectionTitle = {
  fontSize: "11px",
  fontWeight: 700,
  color: colors.gold,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  marginBottom: "10px",
};

function StatutDot({ statut }) {
  const map = {
    confirme: { color: colors.green, char: "✓" },
    en_attente: { color: "#FB923C", char: "⏳" },
    refuse: { color: colors.rose, char: "✗" },
    remplace: { color: colors.textMuted, char: "↻" },
  };
  const cfg = map[statut] || map.en_attente;
  return (
    <span style={{ color: cfg.color, fontSize: "14px" }}>{cfg.char}</span>
  );
}
